import { format } from 'date-fns';
import React, { useState } from 'react';
import { Edit2, Loader, X } from 'react-feather';

import {
  useCreateOrderMemo,
  useDeleteOrderMemo,
  useUpdateOrderMemo,
} from '@/Order/hooks/useOrderUpdate';
import { MemoSectionProps, OrderDetailMemo } from '@/Order/types/orderTypes';

const MemoSection: React.FC<MemoSectionProps> = ({ orderData, onRefetch }) => {
  const [newMemoContent, setNewMemoContent] = useState('');
  const [editingMemo, setEditingMemo] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const createMemoMutation = useCreateOrderMemo();
  const updateMemoMutation = useUpdateOrderMemo();
  const deleteMemoMutation = useDeleteOrderMemo();

  const internalMemos = orderData.orderMemos.filter(
    (memo) => memo.type === 'INTERNAL',
  );

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'yyyy-MM-dd HH:mm:ss');
  };

  const handleCreateMemo = async () => {
    if (!newMemoContent.trim()) return;

    setIsLoading(true);
    try {
      await createMemoMutation.mutateAsync({
        orderUuid: orderData.uuid,
        data: {
          content: newMemoContent.trim(),
          type: 'INTERNAL',
        },
      });

      setNewMemoContent('');
      onRefetch();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || '메모 등록 중 오류가 발생했습니다.';
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartEdit = (memo: OrderDetailMemo) => {
    setEditingMemo(memo.orderMemoUuid);
    setEditContent(memo.content);
  };

  const handleCancelEdit = () => {
    setEditingMemo(null);
    setEditContent('');
  };

  const handleUpdateMemo = async (memoUuid: string) => {
    if (!editContent.trim()) return;

    setIsLoading(true);
    try {
      await updateMemoMutation.mutateAsync({
        orderMemoUuid: memoUuid,
        data: {
          content: editContent.trim(),
          type: 'INTERNAL',
        },
      });

      setEditingMemo(null);
      setEditContent('');
      onRefetch();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || '메모 수정 중 오류가 발생했습니다.';
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteMemo = async (memoUuid: string) => {
    setIsLoading(true);
    try {
      await deleteMemoMutation.mutateAsync(memoUuid);
      onRefetch();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || '메모 삭제 중 오류가 발생했습니다.';
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col rounded border border-gray-200 bg-white shadow-sm">
      <div className="p-5">
        <h3 className="text-base font-medium text-[#1D1D1D]">Memo</h3>
        <textarea
          placeholder="enter your memo here"
          className="mt-3 h-20 w-full rounded border border-[#DDDDDD] p-2 text-sm placeholder-gray-400 focus:outline-none disabled:bg-gray-50"
          value={newMemoContent}
          onChange={(e) => setNewMemoContent(e.target.value)}
          disabled={isLoading}
        />
        <div className="mt-2 flex justify-end">
          <button
            type="button"
            className="flex h-7 cursor-pointer items-center gap-2 rounded bg-[#3E3E3E] px-3 py-1 text-sm font-medium text-white hover:bg-opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleCreateMemo}
            disabled={isLoading || !newMemoContent.trim()}
          >
            {isLoading && <Loader size={12} className="animate-spin" />}
            Post
          </button>
        </div>
      </div>

      {/* Posted Memos */}
      {internalMemos.map((memo) => (
        <div key={memo.orderMemoUuid} className="border-t border-[#DDDDDD] p-5">
          <div className="rounded bg-[#F6F6F6] p-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-[#666666]">
                {formatDate(memo.createdAt)}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="cursor-pointer bg-transparent disabled:opacity-50"
                  onClick={() => handleStartEdit(memo)}
                  disabled={isLoading || editingMemo === memo.orderMemoUuid}
                >
                  <Edit2 size={14} className="text-[#666666]" />
                </button>
                <button
                  type="button"
                  className="cursor-pointer bg-transparent disabled:opacity-50"
                  onClick={() => handleDeleteMemo(memo.orderMemoUuid)}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Loader size={14} className="animate-spin text-[#666666]" />
                  ) : (
                    <X size={16} className="text-[#666666]" />
                  )}
                </button>
              </div>
            </div>
            <hr className="my-3 border-[#DDDDDD]" />

            {editingMemo === memo.orderMemoUuid ? (
              <div className="space-y-2">
                <textarea
                  className="w-full rounded border border-[#CCCCCC] p-2 text-sm focus:outline-none disabled:bg-gray-50"
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  disabled={isLoading}
                  rows={3}
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    className="px-3 py-1 text-xs text-[#666666] hover:text-[#333]"
                    onClick={handleCancelEdit}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="flex items-center gap-1 rounded bg-[#3E3E3E] px-3 py-1 text-xs text-white hover:bg-opacity-80 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={() => handleUpdateMemo(memo.orderMemoUuid)}
                    disabled={isLoading || !editContent.trim()}
                  >
                    {isLoading && <Loader size={10} className="animate-spin" />}
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-[#1D1D1D]">{memo.content}</p>
            )}
          </div>
        </div>
      ))}

      {internalMemos.length === 0 && (
        <div className="border-t border-[#DDDDDD] p-5">
          <p className="text-center text-sm text-[#888888]">No memos yet.</p>
        </div>
      )}
    </div>
  );
};

export default MemoSection;
